const req = require.context(
  "@skv/assets/sprites",
  true,
  /^(.*\.(svg$))[^.]*$/im
);

type LoadedSprite = {
  id: string;
  url: string;
};

const sprites = req.keys().map(function (key) {
  const sprite = req(key);
  return {
    id: sprite.id,
    url: sprite.url,
  } as LoadedSprite;
});

const spritesDictionary = sprites.reduce((acc, cur) => {
  acc[cur.id] = cur;
  return acc;
}, {} as Record<string, LoadedSprite>);

interface SvgSpriteProps
  extends Omit<React.SVGProps<SVGSVGElement>, "aria-hidden" | "role"> {
  id: string;
  ["aria-hidden"]?: never;
}

// SvgSprite should always have either an aria-label or aria-hidden attribute to ensure good accessibility.
// "aria-hidden" is not allowed as a prop, because it will be added if "aria-label" is falsy.

export const SvgSprite: React.FC<SvgSpriteProps> = ({
  id,
  "aria-label": ariaLabel,
  ...rest
}) => {
  const spriteKey = `${id}-usage`;
  const sprite = spritesDictionary[spriteKey];
  const spriteAtLocation = `@skv/assets/sprites/${id.replace(":", "/")}.svg`;

  if (!sprite) {
    throw Error(
      `No sprite found with id ${id}. ${spriteAtLocation} does not exist`
    );
  }

  if (ariaLabel) {
    return (
      <svg
        {...rest}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={ariaLabel}
      >
        <use xlinkHref={sprite.url} />
      </svg>
    );
  }

  return (
    <svg
      {...rest}
      role="img"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <use xlinkHref={sprite.url} />
    </svg>
  );
};
